import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Sparkles, Volume1, Volume2, VolumeX, X } from "lucide-react";
import type { Clip } from "../../types";

interface ClipContextMenuProps {
    clip: Clip;
    onClose: () => void;
    onVolumeChange?: (volume: number) => void;
    onVolumeCommit?: (volume: number) => void;
    onEnhanceAudio?: (clip: Clip) => void;
    onSelectAudioSource?: (clip: Clip, sourceType: 'original' | 'enhanced', sourceId?: string) => void;
    onDeleteAudioSource?: (clip: Clip, sourceId: string) => void;
}

const ClipContextMenu: React.FC<ClipContextMenuProps> = ({
    clip,
    onClose,
    onVolumeChange,
    onVolumeCommit,
    onEnhanceAudio,
    onSelectAudioSource,
    onDeleteAudioSource,
}) => {
    const [localVolume, setLocalVolume] = useState(clip.volume ?? 1);
    const [showAudioSources, setShowAudioSources] = useState(false);

    useEffect(() => {
        setLocalVolume(clip.volume ?? 1);
    }, [clip.id, clip.volume]);

    const VolumeIcon = useMemo(() => {
        if (localVolume > 1) return Volume2;
        if (localVolume > 0) return Volume1;
        return VolumeX;
    }, [localVolume]);

    const isAudioClip = useMemo(() => {
        if (clip.type === 'audio') return true;
        if (clip.type !== 'video') return false;
        const url = clip.url || '';
        return !url.toLowerCase().endsWith('.gif');
    }, [clip]);

    const handleVolumeChange = (value: number) => {
        setLocalVolume(value);
        onVolumeChange?.(value);
    };

    const handleVolumeCommit = () => {
        onVolumeCommit?.(localVolume);
    };

    if (!isAudioClip) return null;

    const hasAudioSources = clip.audioSources && clip.audioSources.length > 0;

    return (
        <div className="h-full flex flex-col bg-white" aria-label="Menu audio du clip">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-sm font-semibold text-gray-900">{clip.name}</h2>
                <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
                <section className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <VolumeIcon className="w-5 h-5 text-gray-600" />
                            <h3 className="text-sm font-semibold text-gray-800">Volume</h3>
                        </div>
                        <span className="text-xs text-gray-500 font-mono">{Math.round(localVolume * 100)}%</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.01"
                        value={localVolume}
                        onChange={(e) => handleVolumeChange(Number((e.target as HTMLInputElement).value))}
                        onMouseUp={handleVolumeCommit}
                        onTouchEnd={handleVolumeCommit}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-black [&::-webkit-slider-thumb]:rounded-full"
                    />
                    <p className="text-xs text-gray-500">Ajustez le niveau sonore du clip pour l'équilibrer avec le reste du montage.</p>
                </section>

                <section className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">Amélioration du son</h3>
                        {clip.isEnhancingAudio && (
                            <span className="text-[11px] text-purple-600 font-semibold uppercase tracking-wide">En cours</span>
                        )}
                    </div>
                    <button
                        onClick={() => onEnhanceAudio?.(clip)}
                        disabled={clip.isEnhancingAudio}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                        <Sparkles className="w-4 h-4" />
                        {clip.isEnhancingAudio ? 'Amélioration en cours...' : 'Améliorer le son'}
                    </button>

                    {hasAudioSources && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setShowAudioSources(!showAudioSources)}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <span>Sources audio ({clip.audioSources!.length})</span>
                                <ChevronDown className={`w-4 h-4 transition-transform ${showAudioSources ? 'rotate-180' : ''}`} />
                            </button>

                            {showAudioSources && (
                                <div className="divide-y divide-gray-200">
                                    {clip.audioSources!.map((source, idx) => {
                                        const isActive = (clip.activeAudioSource === source.id) ||
                                            (clip.activeAudioSource === source.type && !source.id);
                                        const isOriginal = source.type === 'original';

                                        return (
                                            <div key={source.id || idx} className="p-3 space-y-2 bg-white">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-700 font-medium">
                                                            {source.label || (isOriginal ? '🎵 Original' : '✨ Amélioré')}
                                                        </span>
                                                        {source.createdAt && !isOriginal && (
                                                            <span className="text-[10px] text-gray-400">
                                                                {new Date(source.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isActive && <span className="text-xs font-bold text-purple-600">ACTIF</span>}
                                                </div>
                                                <audio controls className="w-full h-8" onClick={(e) => e.stopPropagation()}>
                                                    <source src={source.url} type="audio/wav" />
                                                </audio>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => onSelectAudioSource?.(clip, source.type, source.id)}
                                                        className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                                                    >
                                                        {isActive ? 'Source active' : 'Activer'}
                                                    </button>
                                                    {!isOriginal && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (source.id) onDeleteAudioSource?.(clip, source.id);
                                                            }}
                                                            className="px-3 py-2 rounded-md bg-red-50 text-red-600 hover:bg-red-100 border border-transparent hover:border-red-200 transition-colors"
                                                            title="Supprimer cette version"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default ClipContextMenu;
